import type { PhysioConfiguration, VocabularyEntries, VocabularyKind } from '../api/types';
import { exerciseMediaLabel, exerciseUsageLabel } from '../exercises';

/** One of the lists that are edited an entry at a time: what it is called, and where the configuration answer keeps it. */
export interface ListDefinition {
  kind: VocabularyKind;
  key: 'mechanisms' | 'activityContexts' | 'injuryTypes' | 'bodyRegions' | 'treatmentTypes' | 'exercises' | 'investigationTypes' | 'supportTypes' | 'returnCriteria';
  title: string;
  description: string;
  /** One entry, as a sentence names it: "mechanism". */
  noun: string;
  ordered: boolean;
  /** An entry may belong to one sport (none: every sport's), so the list can be narrowed to a sport and an entry is added for one. */
  sportScoped?: boolean;
}

export type ListEntry = VocabularyEntries[VocabularyKind];

export const LISTS: ListDefinition[] = [
  {
    kind: 'mechanisms',
    key: 'mechanisms',
    title: 'Injury mechanisms',
    description: 'How an injury happened. One for every sport, or one for a single sport: a form offers the general ones and the sport’s own. Contact mechanisms are marked.',
    noun: 'mechanism',
    ordered: true,
    sportScoped: true,
  },
  {
    kind: 'activity-contexts',
    key: 'activityContexts',
    title: 'Activities',
    description: 'What the athlete was doing when they were hurt: Training for every sport, a match, a race or a pool session for the sports that have them.',
    noun: 'activity',
    ordered: true,
    sportScoped: true,
  },
  { kind: 'injury-types', key: 'injuryTypes', title: 'Injury types', description: 'What kind of injury it is.', noun: 'injury type', ordered: true },
  {
    kind: 'body-regions',
    key: 'bodyRegions',
    title: 'Body regions',
    description: 'Where an injury is. The same body map serves every sport. Regions marked left/right are recorded with a side.',
    noun: 'body region',
    ordered: true,
  },
  {
    kind: 'treatment-types',
    key: 'treatmentTypes',
    title: 'Treatment types',
    description: 'Offered when a treatment is recorded. Whoever records a treatment can also add a type where they choose it.',
    noun: 'treatment type',
    ordered: true,
  },
  {
    kind: 'investigation-types',
    key: 'investigationTypes',
    title: 'Investigation types',
    description: 'Scans, reports and tests an injury can be investigated with (MRI, CT, X-ray...). Whoever records investigations can add one where they choose it.',
    noun: 'investigation type',
    ordered: true,
  },
  {
    kind: 'support-types',
    key: 'supportTypes',
    title: 'Support types',
    description: 'Support given besides treatment and rehabilitation: taping, braces, slings, walking aids. Offered under "Other" in injury management.',
    noun: 'support type',
    ordered: true,
  },
  {
    kind: 'return-criteria',
    key: 'returnCriteria',
    title: 'Return-to-play criteria',
    description: 'What a sport checks before an athlete returns, beyond pain, swelling, range of motion and function: contact tolerance and tackling for Rugby, bowling for Cricket.',
    noun: 'criterion',
    ordered: true,
    sportScoped: true,
  },
  {
    kind: 'exercises',
    key: 'exercises',
    title: 'Exercise library',
    description: 'One shared library for rehabilitation, gym, warm-up, conditioning and other exercise work, including demonstrations and instruction links.',
    noun: 'exercise',
    ordered: false,
  },
];

export const entriesOf = (configuration: PhysioConfiguration, definition: ListDefinition): ListEntry[] => configuration[definition.key];

export function entryName(entry: ListEntry): string {
  return 'label' in entry ? entry.label : entry.name;
}

/** The sport an entry belongs to: its id (none: every sport's) and name. Only the sport-aware lists have one. */
export function entrySport(entry: ListEntry): { sportId: string | null; sportName: string } | null {
  return 'sportId' in entry ? { sportId: entry.sportId, sportName: entry.sportName } : null;
}

/** The small facts shown beside an entry's name. */
export function entryFacts(kind: VocabularyKind, entry: ListEntry): string[] {
  if (kind === 'mechanisms' && 'contact' in entry && entry.contact) return ['Contact'];
  if (kind === 'body-regions' && 'sided' in entry && entry.sided) return ['Left / right'];
  if (kind === 'exercises' && 'usageCategory' in entry) return [exerciseUsageLabel(entry.usageCategory), ...(entry.category ? [entry.category] : [])];
  return [];
}

/** What a list's entry is searched by: its name, and for an exercise its category and description, for a sport-aware entry its sport. */
export function entryText(entry: ListEntry): string {
  return [
    entryName(entry),
    'usageCategory' in entry ? exerciseUsageLabel(entry.usageCategory) : '',
    'category' in entry ? entry.category : '',
    'description' in entry ? entry.description : '',
    'sportName' in entry ? entry.sportName : '',
  ].join(' ');
}

/** What is said of an entry to someone who may look at it but not change it. */
export function entryDetails(kind: VocabularyKind, entry: ListEntry): { label: string; value: string }[] {
  const details = [{ label: kind === 'activity-contexts' ? 'Activity' : 'Name', value: entryName(entry) }];
  if ('sportName' in entry) details.push({ label: 'Sport', value: entry.sportName || 'Every sport' });
  if (kind === 'mechanisms' && 'contact' in entry) details.push({ label: 'Contact', value: entry.contact ? 'Another person was involved' : 'No contact' });
  if (kind === 'body-regions' && 'sided' in entry) details.push({ label: 'Sides', value: entry.sided ? 'Left and right' : 'One side' });
  if (kind === 'exercises' && 'usageCategory' in entry) {
    details.push(
      { label: 'Use category', value: exerciseUsageLabel(entry.usageCategory) },
      { label: 'Body area or group', value: entry.category || 'Not specified' },
      { label: 'Description', value: entry.description || 'Not specified' },
      { label: 'Demonstration', value: entry.mediaType ? exerciseMediaLabel(entry.mediaType) : 'None' },
    );
  }
  details.push({ label: 'Status', value: entry.active ? 'In use' : 'Archived' }, { label: 'Records using it', value: String(entry.inUse) });
  return details;
}
